import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Divider,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Breadcrumbs,
  Link as MUILink,
  Grid,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch, useSelector } from "react-redux";
import TablePagination from "@mui/material/TablePagination";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CloseIcon from "@mui/icons-material/Close";
import {
  getSellerBuyerInvoices,
  getInvoiceStatement,
  updatePaymentStatus,
} from "../redux/slices/invoiceSlice";
import { toast } from "react-toastify";
import { useLocation, Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";
// Update status colors to handle "Overdue"
const statusColor = (status) => {
  switch (status) {
    case "Paid":
    case "Completed":
      return "success";
    case "Pending":
      return "warning";
    case "Overdue":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  return amount?.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function ViewBuyerLedger() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const id = user?._id;
  const location = useLocation();
  const { buyerId } = location.state || {};

  const [openBankStatement, setOpenBankStatement] = useState(false);
  const [selectedInvoiceForStatement, setSelectedInvoiceForStatement] =
    useState(null);
  const {
    // sellerInvoices = [],
    sellerBuyerInvoices = [],
    loading,
    sellerBuyerPagination,
  } = useSelector((state) => state.invoice);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [statusFilter, setStatusFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Default");

  const [openExportConfirm, setOpenExportConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { statement, statementLoading } = useSelector((state) => state.invoice);

  useEffect(() => {
    if (!id) return;
    let statusParam;
    if (statusFilter === "Default") {
      statusParam = undefined;
    } else if (statusFilter === "All") {
      statusParam = "All";
    } else {
      statusParam = statusFilter;
    }

    dispatch(
      getSellerBuyerInvoices({
        buyer: buyerId,
        seller: id,
        page,
        limit: rowsPerPage,
        ...(statusParam && { status: statusParam }),
      }),
    );
  }, [dispatch, id, page, rowsPerPage, statusFilter]);

  // Process API data
  const processInvoices = () => {
    if (!sellerBuyerInvoices || !Array.isArray(sellerBuyerInvoices)) return [];

    return sellerBuyerInvoices.map((invoice) => ({
      id: invoice.invoiceNumber || `INV${invoice._id?.slice(-6) || "000000"}`,
      invoiceId: invoice?._id,
      date: formatDate(invoice.createdAt),
      buyer: invoice.buyer?.name || "Unknown Buyer",
      orderId: invoice.order?._id || "N/A",
      method: "Credit",
      amount: invoice.amount || 0,
      status: invoice.status || "Pending",
      dueDate: formatDate(invoice.dueDate),
      // New Interest Fields added here
      interestRate: invoice.interestRatePerYear || 0,
      interestStartDate: formatDate(invoice.interestAccrualStartDate),
      latestBalance: invoice.latestBalance || 0,
      orderItems: invoice.order?.items || [],
      originalData: invoice,
    }));
  };

  const processedPayments = processInvoices();
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionPaymentId, setActionPaymentId] = useState(null);

  const filteredPayments = (() => {
    if (statusFilter === "Default") {
      return processedPayments.filter(
        (p) => p.status === "Pending" || p.status === "Overdue",
      );
    }
    if (statusFilter === "All") {
      return processedPayments;
    }
    return processedPayments.filter((p) => p.status === statusFilter);
  })();

  const handleActionsMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setActionPaymentId(id);
  };
  const handleActionsMenuClose = () => {
    setAnchorEl(null);
    setActionPaymentId(null);
  };

  // Open Bank Statement
  const handleBankStatementOpen = (payment) => {
    setSelectedInvoiceForStatement(payment);
    setOpenBankStatement(true);
    handleActionsMenuClose();
  };

  const handleBankStatementClose = () => {
    setOpenBankStatement(false);
    setSelectedInvoiceForStatement(null);
  };

  const handleOpenOrderModal = (row) => {
    setSelectedOrder(row);
    setOpenOrderModal(true);
  };

  const handleCloseOrderModal = () => {
    setOpenOrderModal(false);
    setSelectedOrder(null);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    doc.setFontSize(16);
    doc.text("Invoices & Interest Report", 14, 15);

    const columns = [
      "#",
      "Order ID",
      "Invoice #",
      "Buyer",
      "Amount",
      "Due Date",
      "Interest %",
      "Interest Start",
      "Latest Balance",
      "Status",
    ];

    const rows = filteredPayments.map((row, index) => [
      index + 1,
      row.orderId,
      row.id,
      row.buyer,
      formatCurrency(row.amount),
      row.dueDate,
      `${row.interestRate}%`,
      row.interestStartDate,
      formatCurrency(row.latestBalance),
      row.status,
    ]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 25,
      styles: { fontSize: 9 },
      headStyles: {
        fillColor: [40, 0, 113],
      },
    });

    doc.save(`Invoices_Report_${Date.now()}.pdf`);
    setOpenExportConfirm(false);
  };

  const handleDownloadBankStatementPDF = () => {
    if (!selectedInvoiceForStatement) return;

    const doc = new jsPDF("p", "mm", "a4");

    const invoice = selectedInvoiceForStatement;
    const statement = invoice.originalData.bankStatement || [];

    // ===== HEADER =====
    doc.setFontSize(16);
    doc.setTextColor(40, 0, 113);
    doc.text("ACCOUNT STATEMENT", 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(`Invoice Ref : ${invoice.id}`, 14, 26);
    doc.text(`Buyer       : ${invoice.buyer}`, 14, 32);
    doc.text(`Interest    : ${invoice.interestRate}% p.a.`, 14, 38);
    doc.text(`Due Date    : ${invoice.dueDate}`, 14, 44);
    doc.text(`Generated On : ${new Date().toLocaleString("en-IN")}`, 14, 50);

    // ===== TABLE DATA =====
    const tableBody = statement.map((txn) => [
      formatDate(txn.date),
      txn.description?.toUpperCase() || "-",
      txn.debit > 0 ? formatCurrency(txn.debit) : "",
      txn.credit > 0 ? formatCurrency(txn.credit) : "",
      formatCurrency(txn.balance),
    ]);

    autoTable(doc, {
      startY: 56,
      head: [["Txn Date", "Narration", "Debit", "Credit", "Balance"]],
      body: tableBody,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [238, 242, 248],
        textColor: 0,
        fontStyle: "bold",
      },
      columnStyles: {
        2: { halign: "right", textColor: [211, 47, 47] },
        3: { halign: "right", textColor: [46, 125, 50] },
        4: { halign: "right", fontStyle: "bold" },
      },
    });

    // FOOTER
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      "This is a system generated statement. No signature required.",
      14,
      finalY,
    );

    doc.save(`Bank_Statement_${invoice.id}.pdf`);
  };

  const calculateInterestFromStatement = (bankStatement = [], principal) => {
    const interestEntries = bankStatement.filter(
      (e) => e.description?.toLowerCase().includes("interest") && e.debit > 0,
    );

    const totalInterest = interestEntries.reduce((sum, e) => sum + e.debit, 0);

    return {
      days: interestEntries.length,
      interest: Math.round(totalInterest * 100) / 100,
      total: Math.round((principal + totalInterest) * 100) / 100,
    };
  };

  const handleDownloadDateRangeStatement = () => {
    const invoice = selectedInvoiceForStatement;
    if (!invoice || !fromDate || !toDate) return;

    const doc = new jsPDF();
    const allTxns = invoice.originalData.bankStatement || [];

    const filtered = allTxns.filter((t) => {
      const d = new Date(t.date);
      return d >= new Date(fromDate) && d <= new Date(toDate);
    });

    autoTable(doc, {
      head: [["Date", "Narration", "Debit", "Credit", "Balance"]],
      body: filtered.map((t) => [
        formatDate(t.date),
        t.description,
        t.debit || "",
        t.credit || "",
        formatCurrency(t.balance),
      ]),
    });

    doc.save(`Statement_${fromDate}_to_${toDate}.pdf`);
  };

  useEffect(() => {
    if (openBankStatement && selectedInvoiceForStatement?.invoiceId) {
      dispatch(getInvoiceStatement(selectedInvoiceForStatement?.invoiceId));
    }
  }, [openBankStatement, selectedInvoiceForStatement, dispatch]);

  const [editIndex, setEditIndex] = useState(null);
  const [editedCredit, setEditedCredit] = useState("");

  const handleCancel = () => {
    setEditIndex(null);
    setEditedCredit("");
  };

  const handleStatusUpdate = async (index, status) => {
    const item = statement[index];
    if (!item?._id) return;

    try {
      await dispatch(
        updatePaymentStatus({
          bankStatementId: item._id,
          paymentStatus: status,
        }),
      ).unwrap();

      toast.success(`Payment status updated to ${status}`);
      dispatch(
        getSellerBuyerInvoices({
          buyer: buyerId,
          seller: id,
          page,
          limit: rowsPerPage,
        }),
      );
      setOpenBankStatement(false);
    } catch (err) {
      toast.error(err?.message || "Failed to update payment status");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageTitle
        title="Pending Invoices & Interest Report - BizBridge"
        description="View and manage pending invoices for a specific buyer with detailed interest calculation. Track buyer-wise dues, monitor overdue payments, and manage credit efficiently."
        keywords="buyer pending invoices, buyer wise dues, interest report, credit management, overdue payments, invoice tracking, buyer payments, interest calculation, B2B buyer payments, credit terms"
        url="/seller-dashboard/buyer-pending-invoices"
      />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 0, md: 2 },
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            width: "fit-content",
            backgroundColor: "#fff",
            ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/seller-dashboard/buyer-network"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Buyer Network
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            Pending Invoices
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="center" mb={3}>
            <Grid item xs={12} md={6}>
              <Typography fontWeight={700} fontSize={22}>
                Pending Invoices & Interest Report
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <Stack direction="row" spacing={1}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="Default">Default</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  sx={{ borderRadius: 2, bgcolor: "#fff" }}
                  onClick={() => setOpenExportConfirm(true)}
                >
                  Export
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{ borderRadius: 2, mb: 3, overflowX: "auto" }}
          >
            {!isMobile ? (
              <TableContainer>
                <Table sx={{ overflowX: "auto" }}>
                  <TableHead>
                    <TableRow sx={{ background: "#f6f8fa" }}>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      {/* <TableCell sx={{ fontWeight: 700 }}>OrderId</TableCell> */}
                      <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Buyer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Amount (₹)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#d32f2f" }}>
                        Interest Rate
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#d32f2f" }}>
                        Latest Balance
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((row, index) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600 }}
                          onClick={() => handleOpenOrderModal(row)}
                        >
                          <Button
                            variant="text"
                            sx={{
                              fontWeight: 700,
                              textTransform: "none",
                              p: 0,
                            }}
                          >
                            {row.id}
                          </Button>

                          <Typography
                            variant="caption"
                            display="block"
                            color="textSecondary"
                            sx={{
                              fontWeight: 700,
                              textTransform: "none",
                              p: 0,
                              cursor: "pointer",
                            }}
                          >
                            {row.date}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.buyer}</TableCell>
                        <TableCell>₹{formatCurrency(row.amount)}</TableCell>
                        <TableCell>{row.dueDate}</TableCell>
                        {/* Interest Data Display */}
                        <TableCell>
                          <Chip
                            label={`${row.interestRate}% p.a.`}
                            size="small"
                            variant="outlined"
                            color="error"
                            sx={{
                              borderColor: "#ffcdd2",
                              color: "#c62828",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatCurrency(row.latestBalance)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={row.status}
                            color={statusColor(row.status)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell>
                          <IconButton
                            onClick={(e) => handleActionsMenuOpen(e, row.id)}
                            size="small"
                          >
                            <MoreHorizIcon />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={
                              actionPaymentId === row.id && Boolean(anchorEl)
                            }
                            onClose={handleActionsMenuClose}
                          >
                            <MenuItem
                              onClick={() => handleBankStatementOpen(row)}
                            >
                              <ListItemIcon>
                                <AccountBalanceIcon fontSize="small" />
                              </ListItemIcon>{" "}
                              Statement
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={sellerBuyerPagination?.totalInvoices || 0}
                  page={(sellerBuyerPagination?.currentPage || 1) - 1}
                  onPageChange={(event, newPage) => {
                    setPage(newPage + 1);
                  }}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </TableContainer>
            ) : (
              // Mobile View
              <Box>
                {filteredPayments.map((row, index) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography fontWeight={700}>{index + 1}</Typography>
                        <Typography fontWeight={700}>
                          {" "}
                          Invoice# {row.id}
                        </Typography>
                        <Chip
                          label={row.status}
                          color={statusColor(row.status)}
                          size="small"
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Buyer: {row.buyer}
                      </Typography>
                      <Typography variant="body2">
                        Amount: ₹{formatCurrency(row.amount)}
                      </Typography>

                      <Typography
                          variant="body2"
                        >
                          Latest Balance: {formatCurrency(row.latestBalance)}
                        </Typography>

                      {/* Interest details in mobile */}
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          bgcolor: "#fff5f5",
                          borderRadius: 1,
                          border: "1px dashed #ffcdd2",
                        }}
                      > 
                        <Typography
                          variant="caption"
                          color="error"
                          fontWeight={600}
                          display="block"
                        >
                          Interest: {row.interestRate}% per month
                        </Typography>
                        
                        <Typography variant="caption" color="textSecondary">
                          Accrual Starts: {row.interestStartDate}
                        </Typography>
                      </Box>

                      <Stack direction="row" justifyContent="flex-end" mt={1}>
                        <Button
                          size="small"
                          startIcon={<AccountBalanceIcon />}
                          onClick={() => handleBankStatementOpen(row)}
                        >
                          Statement
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                <TablePagination
                  component="div"
                  count={sellerBuyerPagination?.totalInvoices || 0}
                  page={(sellerBuyerPagination?.currentPage || 1) - 1}
                  onPageChange={(event, newPage) => {
                    setPage(newPage + 1);
                  }}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </Box>
            )}
          </Paper>

          {/* ORDER MODAL FOR ORDER DETAILS */}
          <Dialog
            open={openOrderModal}
            onClose={handleCloseOrderModal}
            maxWidth="md"
            fullWidth
          >
            <DialogContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Order Details – Invoice #{selectedOrder?.id}
              </Typography>

              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Price (₹)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Total (₹)
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedOrder?.orderItems?.map((item, index) => (
                      <TableRow key={item._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.name}</TableCell>

                        <TableCell>
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{
                              width: 50,
                              height: 50,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ddd",
                            }}
                          />
                        </TableCell>

                        <TableCell align="right">{item.quantity}</TableCell>

                        <TableCell align="right">
                          ₹{formatCurrency(item.price)}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ₹{formatCurrency(item.finalPrice)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: "right" }}>
                <Typography fontWeight={700}>
                  Order Total: ₹{formatCurrency(selectedOrder?.amount)}
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseOrderModal} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* BANK STATEMENT DIALOG */}
          <Dialog
            open={openBankStatement}
            onClose={handleBankStatementClose}
            fullWidth
            maxWidth="lg"
          >
            <Box sx={{ bgcolor: "#fff", p: 0 }} ref={printRef}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: "4px solid #0b4064",
                  background: "#fcfcfc",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#0b4064",
                      fontWeight: 700,
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AccountBalanceIcon fontSize="large" /> ACCOUNT STATEMENT
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Generated on: {new Date().toLocaleString("en-IN")}
                  </Typography>
                </Box>

                {selectedInvoiceForStatement && (
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      INVOICE REF: {selectedInvoiceForStatement.id}
                    </Typography>
                    <Typography variant="body2">
                      Buyer: {selectedInvoiceForStatement.buyer}
                    </Typography>
                    <Typography variant="body2">
                      Interest Rate: {selectedInvoiceForStatement.interestRate}%
                      p.a.
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedInvoiceForStatement &&
                (() => {
                  const bankStatement =
                    selectedInvoiceForStatement.originalData.bankStatement;

                  const summary = calculateInterestFromStatement(
                    bankStatement,
                    selectedInvoiceForStatement.amount,
                  );

                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid #ffd6d6",
                        borderRadius: 2,
                        background: "#fff5f5",
                      }}
                    >
                      <Stack direction="row" spacing={4} flexWrap="wrap">
                        <Typography fontWeight={600}>
                          Due Date : {selectedInvoiceForStatement.dueDate}
                        </Typography>

                        <Typography fontWeight={600}>
                          Days Passed: {summary.days}
                        </Typography>

                        <Typography fontWeight={600}>
                          Interest Till Today: ₹
                          {formatCurrency(summary.interest)}
                        </Typography>

                        <Typography fontWeight={700} color="error">
                          Total Payable: ₹{formatCurrency(summary.total)}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })()}

              <DialogContent sx={{ p: { xs: 1, md: 4 }, bgcolor: "#fff" }}>
                {/* DATE RANGE TOOLBAR */}
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    background: "#fafafa",
                  }}
                >
                  <TextField
                    type="date"
                    size="small"
                    label="From Date"
                    InputLabelProps={{ shrink: true }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />

                  <TextField
                    type="date"
                    size="small"
                    label="To Date"
                    InputLabelProps={{ shrink: true }}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    disabled={!fromDate || !toDate}
                    onClick={handleDownloadDateRangeStatement}
                    sx={{ ml: "auto" }}
                  >
                    Date Range PDF
                  </Button>
                </Box>
                {selectedInvoiceForStatement?.originalData?.bankStatement
                  ?.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: "1px solid #ccc" }}
                  >
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#eef2f8", height: "40px" }}>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                              borderRight: "1px solid #ddd",
                            }}
                          >
                            Txn Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                              borderRight: "1px solid #ddd",
                            }}
                          >
                            Narration / Description
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                              borderRight: "1px solid #ddd",
                            }}
                          >
                            Debit (₹)
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                              borderRight: "1px solid #ddd",
                            }}
                          >
                            Credit (₹)
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: "#333" }}
                          >
                            Balance (₹)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoiceForStatement.originalData.bankStatement.map(
                          (entry, index) => (
                            <TableRow
                              key={entry._id || index}
                              sx={{
                                "&:nth-of-type(even)": { bgcolor: "#fbfbfb" },
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  borderRight: "1px solid #eee",
                                  fontSize: "13px",
                                }}
                              >
                                {formatDate(entry.date)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: "1px solid #eee",
                                  fontSize: "13px",
                                  textTransform: "uppercase",
                                }}
                              >
                                {entry.description}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  borderRight: "1px solid #eee",
                                  fontSize: "13px",
                                  color: "#d32f2f",
                                  fontWeight: entry.debit > 0 ? 600 : 400,
                                }}
                              >
                                {entry.debit > 0
                                  ? formatCurrency(entry.debit)
                                  : ""}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  borderRight: "1px solid #eee",
                                  fontSize: "13px",
                                  color: "#2e7d32",
                                  fontWeight: entry.credit > 0 ? 600 : 400,
                                }}
                              >
                                {entry.credit > 0
                                  ? formatCurrency(entry.credit)
                                  : ""}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontSize: "13px", fontWeight: 700 }}
                              >
                                {formatCurrency(entry.balance)}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 5,
                      color: "#888",
                      border: "1px dashed #ccc",
                      borderRadius: 2,
                    }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 40, mb: 1, opacity: 0.5 }}
                    />
                    <Typography>
                      No transactions recorded for this invoice yet.
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <Divider />

              <>
                {statement?.length > 0 && (
                  <Box sx={{ px: 2, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Payment / Credit Entries
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      These credit entries were added by the buyer and are
                      awaiting your approval before being posted to the bank
                      statement.
                    </Typography>
                  </Box>
                )}

                {statement?.length > 0 &&
                  statement.map((item, index) => {
                    if (!item?.credit || item.credit < 0) return null;

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {/* CREDIT */}
                        {editIndex === index ? (
                          <TextField
                            size="small"
                            label="Edit Credit Amount"
                            type="number"
                            value={editedCredit}
                            onChange={(e) => setEditedCredit(e.target.value)}
                          />
                        ) : (
                          <Typography variant="body2">
                            Credit Amount (₹): {item.credit}
                          </Typography>
                        )}

                        {/* ACTION BUTTONS */}
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {editIndex === index ? (
                            <>
                              {/* <IconButton
                      onClick={() => handleSave(index)}
                      color="success"
                    >
                      <CheckIcon />
                    </IconButton> */}
                              <IconButton onClick={handleCancel} color="error">
                                <CloseIcon />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              {/* Seller Status Buttons */}
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() =>
                                  handleStatusUpdate(index, "Approved")
                                }
                              >
                                Approved
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleStatusUpdate(index, "Cancelled")
                                }
                              >
                                Cancelled
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </>

              <DialogActions sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                <Button
                  onClick={handleBankStatementClose}
                  variant="outlined"
                  color="primary"
                >
                  Close Statement
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadBankStatementPDF}
                >
                  Download PDF
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        </Box>

        <Dialog
          open={openExportConfirm}
          onClose={() => setOpenExportConfirm(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent sx={{ textAlign: "center", py: 3 }}>
            <DownloadIcon sx={{ fontSize: 48, color: "#0b4064", mb: 1 }} />

            <Typography variant="h6" fontWeight={700} gutterBottom>
              Download PDF?
            </Typography>

            <Typography variant="body2" color="textSecondary">
              Are you sure you want to export the current invoice list as a PDF
              file?
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpenExportConfirm(false)}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleExportPDF}
              disabled={exporting}
            >
              {exporting ? "Downloading..." : "Yes, Download"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
